import { createSystem, defaultConfig, defineConfig, defineGlobalStyles } from '@chakra-ui/react';
import { tokens } from './tokens';
import { semanticTokens } from './semantic-tokens';
import { textStyles } from './textStyles';
import {
  buttonRecipe,
  iconButtonRecipe,
  linkRecipe,
  checkboxRecipe,
  inputRecipe,
  textareaRecipe,
} from './recipes/index';
import {
  dialogSlotRecipe,
  toastSlotRecipe,
  tooltipSlotRecipe,
  selectSlotRecipe,
  nativeSelectSlotRecipe,
  switchSlotRecipe,
} from './slot-recipes';
import { breakpoints } from './breakpoints';

/**
 * `createSystem` deep-merges configs. Strip preset pieces we replace entirely so the app theme
 * is the single source of truth: `textStyles`, primitive `tokens.colors`, `semanticTokens.colors`,
 * and preset `recipes` we override (`button` — their defaults would otherwise stay merged
 * and override our base styles, e.g. Chakra input `variant: outline` + `borderColor: border`).
 */
const defaultTheme = defaultConfig.theme;
if (defaultTheme?.recipes == null || defaultTheme.tokens == null) {
  throw new Error('Chakra defaultConfig.theme is incomplete');
}
const themeWithoutPresetOverrides = { ...defaultTheme };
delete themeWithoutPresetOverrides.textStyles;

const tokensWithoutPresetColors = { ...themeWithoutPresetOverrides.tokens };
delete tokensWithoutPresetColors.colors;

const semanticWithoutPresetColors = themeWithoutPresetOverrides.semanticTokens
  ? { ...themeWithoutPresetOverrides.semanticTokens }
  : {};
delete semanticWithoutPresetColors.colors;
delete semanticWithoutPresetColors.shadows;

const presetRecipesStripped = { ...themeWithoutPresetOverrides.recipes };
delete presetRecipesStripped.button;
delete presetRecipesStripped.textarea;

const presetSlotRecipesStripped = { ...themeWithoutPresetOverrides.slotRecipes };
delete presetSlotRecipesStripped.switch;

const strippedDefaultConfig = {
  ...defaultConfig,
  theme: {
    ...themeWithoutPresetOverrides,
    tokens: tokensWithoutPresetColors,
    semanticTokens: semanticWithoutPresetColors,
    recipes: presetRecipesStripped,
    slotRecipes: presetSlotRecipesStripped,
  },
};

/** Preset `*::selection` uses `colorPalette.emphasized` with `html { colorPalette: gray }`; our tokens use `grey` and we don’t ship a `gray` palette — override so selection is visible. */
const selectionFix = defineGlobalStyles({
  '*::selection': {
    bg: '#b3d4fc',
    color: 'text.primary',
  },
  '*:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
});

const config = defineConfig({
  theme: {
    breakpoints,
    tokens,
    semanticTokens,
    textStyles,
    recipes: {
      button: buttonRecipe,
      iconButton: iconButtonRecipe,
      link: linkRecipe,
      checkbox: checkboxRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
    },
    slotRecipes: {
      dialog: dialogSlotRecipe,
      toast: toastSlotRecipe,
      tooltip: tooltipSlotRecipe,
      nativeSelect: nativeSelectSlotRecipe,
      select: selectSlotRecipe,
      switch: switchSlotRecipe,
    },
  },
  globalCss: selectionFix,
});

export const system = createSystem(strippedDefaultConfig, config);
